import { Component, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import logoDark from './assets/navartha-logo-dark.png';
import Lenis from 'lenis';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollManager from './components/ScrollManager';
import Home from './pages/Home';
import ClinicPage from './pages/ClinicPage';
import ReceivedPage from './pages/ReceivedPage';
import PrivacyPage from './pages/PrivacyPage';
import '@/App.css';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }
  static getDerivedStateFromError() {
    return { error: true };
  }
  render() {
    if (this.state.error) {
      const isId = window.location.pathname.startsWith('/id');
      const copy = isId
        ? {
            title: 'Sepertinya halaman ini belum berhasil terbuka.',
            hint: 'Coba muat ulang untuk melanjutkan.',
            retry: 'Coba Lagi',
            home: 'Kembali ke Beranda',
          }
        : {
            title: "This page didn't open as expected.",
            hint: 'Reload the page to continue.',
            retry: 'Try Again',
            home: 'Back to Home',
          };
      return (
        <div role="alert" className="flex min-h-screen flex-col items-center justify-center bg-bone px-6 text-center">
          <img src={logoDark} alt="Navartha" className="h-10 w-auto" data-testid="error-logo" />
          <h1 className="mt-8 font-tight text-2xl font-semibold tracking-[-0.02em] text-midnight">{copy.title}</h1>
          <p className="mt-3 max-w-sm text-[14px] leading-relaxed text-midnight/70">{copy.hint}</p>
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row">
            <button
              type="button"
              onClick={() => window.location.reload()}
              data-testid="error-retry"
              className="bg-vermilion px-7 py-3.5 text-[13px] font-medium tracking-[0.06em] text-bone transition-colors duration-300 hover:bg-[#A8402C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vermilion"
            >
              {copy.retry}
            </button>
            <a
              href="/"
              data-testid="error-home"
              className="border border-midnight/25 px-7 py-3.5 text-[13px] font-medium tracking-[0.06em] text-midnight transition-colors duration-300 hover:border-midnight hover:bg-midnight hover:text-bone focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vermilion"
            >
              {copy.home}
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function LenisSetup() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

const Page = ({ lang, children }) => (
  <>
    <LenisSetup />
    <Header lang={lang} />
    <ScrollManager />
    <main data-testid="page-main">{children}</main>
    <Footer lang={lang} />
    <div className="grain" aria-hidden="true" />
  </>
);

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Page lang="en"><Home lang="en" /></Page>} />
          <Route path="/id" element={<Page lang="id"><Home lang="id" /></Page>} />
          <Route path="/business-clinic" element={<Page lang="en"><ClinicPage lang="en" /></Page>} />
          <Route path="/id/business-clinic" element={<Page lang="id"><ClinicPage lang="id" /></Page>} />
          <Route path="/business-clinic/received" element={<Page lang="en"><ReceivedPage lang="en" /></Page>} />
          <Route path="/id/business-clinic/received" element={<Page lang="id"><ReceivedPage lang="id" /></Page>} />
          <Route path="/privacy" element={<Page lang="en"><PrivacyPage lang="en" /></Page>} />
          <Route path="/id/privacy" element={<Page lang="id"><PrivacyPage lang="id" /></Page>} />
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
